import { MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * One button and a menu, instead of six things at the same weight.
 *
 * ## What went wrong without it
 *
 * A row on Power & tokens carried up to six inline text actions — File it,
 * Correct, Edit, Retry, It failed, Details, Landlord — all styled the same,
 * all equally prominent, wrapping onto a second line on a narrow row. Nothing
 * told the eye which was the usual one, so every row had to be read rather
 * than recognised, and "It failed" sat at the same weight as "Details".
 *
 * ## The rules, and why each
 *
 * **One primary.** The action somebody came to the row to take, as a real
 * button. If a row has no obvious primary, it has none and everything goes in
 * the menu — inventing one is how a destructive action ends up under the
 * cursor.
 *
 * **Everything else behind `⋯`.** Not hidden: one press away, and the same
 * press on every row of every table, which is the whole value. Muscle memory
 * only forms if the position is stable.
 *
 * **Destructive last, and separated.** A rule about geometry rather than
 * colour: red text is not protection when the mouse is already moving. The
 * separator costs a few pixels and buys a moment.
 *
 * **Order is caller-declared, not sorted here.** Sorting would put a
 * different verb first on each table and destroy the thing this is for.
 */
export type RowAction = {
  label: string;
  onClick: () => void;
  /** Drawn as the button rather than in the menu. At most one per row. */
  primary?: boolean;
  /** Sent to the bottom, behind a separator. */
  danger?: boolean;
  disabled?: boolean;
  /** Why it is disabled, or what it does. Never left empty on a disabled one. */
  title?: string;
  /**
   * A stable name for this action, independent of its label.
   *
   * Rendered as `data-action-id`. Labels are written for people and get
   * rewritten; a test that presses "Send on its way" breaks when somebody
   * improves the wording, which teaches everybody that the tests are noise.
   */
  id?: string;
};

export function RowActions({ actions }: { actions: RowAction[] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  /*
   * Closing on an outside press and on Escape. A menu that stays open while
   * somebody works elsewhere on the page covers the next row down, which is
   * usually the row they were reaching for.
   */
  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const usable = actions.filter(Boolean);
  if (usable.length === 0) return null;

  const primary = usable.find((one) => one.primary && !one.disabled) ?? null;
  const rest = usable.filter((one) => one !== primary);

  /* Destructive to the bottom, otherwise the caller's order is kept. */
  const ordered = [...rest.filter((one) => !one.danger), ...rest.filter((one) => one.danger)];
  const firstDanger = ordered.findIndex((one) => one.danger);

  return (
    <div className="flex items-center justify-end gap-1 print:hidden" ref={box}>
      {primary && (
        <button
          type="button"
          className="btn-primary px-2 py-1 text-xs"
          title={primary.title}
          onClick={primary.onClick}
        >
          {primary.label}
        </button>
      )}

      {ordered.length > 0 && (
        <div className="relative">
          <button
            type="button"
            className="btn-quiet px-2 py-1 text-xs"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label="More actions"
            title="More actions"
            onClick={() => setOpen(!open)}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>

          {open && (
            <div
              role="menu"
              /*
               * Right-aligned and above the rows below it. Anchored to the
               * button rather than the row, so it opens in the same place
               * whatever the row height.
               */
              className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-border bg-card py-1 shadow-card"
            >
              {ordered.map((action, index) => (
                <div key={action.label}>
                  {index === firstDanger && firstDanger > 0 && (
                    <div className="my-1 border-t border-border" aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    data-action-id={action.id}
                    disabled={action.disabled}
                    title={action.title}
                    className={`flex w-full items-center px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                      action.danger ? 'text-danger-700 hover:bg-danger-50' : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      setOpen(false);
                      action.onClick();
                    }}
                  >
                    {action.label}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
